export type CertificateStatus = "active" | "expiring" | "expired" | "pending" | "revoked";
export type CertificateType =
  | "firma_natural"
  | "firma_natural_rut"
  | "firma_representante"
  | "firma_juridica"
  | "facturacion"
  | "funcion_publica"
  | "profesional"
  | "funcion_publica_siif"
  | "comunidad_academica";

export type Certificate = {
  id: string;
  type: CertificateType;
  holder: string;
  documentNumber: string;
  serial: string;
  status: CertificateStatus;
  issuedAt: string;
  expiresAt: string;
  issuer: string;
  validityYears: 1 | 2 | 3;
};

export type DocType = "cedula" | "rut" | "camara_comercio" | "poder" | "otro";

export type UploadedDocument = {
  id: string;
  name: string;
  type: DocType;
  sizeBytes: number;
  uploadedAt: string;
};

export type TimelineEvent = {
  id: string;
  at: string;
  kind: "requested" | "docs_verified" | "csr_generated" | "issued" | "downloaded" | "renewed" | "revoked";
  summary: string;
};

export type Activity = {
  id: string;
  at: string;
  icon: "upload" | "download" | "issued" | "reminder" | "request";
  text: string;
};

const daysFromNow = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
};

export const mockUser = {
  id: "u_42",
  firstName: "María",
  lastName: "Gómez",
  documentType: "CC",
  documentNumber: "1.045.678.900",
  email: "maria.gomez@ejemplo.com",
  phone: "+57 315 000 0000",
  city: "Barranquilla",
  department: "Atlántico",
  address: "Carrera 53 # 82 - 10",
  kind: "natural" as "natural" | "juridica",
  companyName: null as string | null,
  avatarSeed: "MG",
  memberSince: "2024-03-12",
};

export const mockCertificates: Certificate[] = [
  {
    id: "c_01",
    type: "firma_natural",
    holder: "María Fernanda Gómez",
    documentNumber: "1.045.678.900",
    serial: "7E:2A:3B:C4:D5:11",
    status: "active",
    issuedAt: daysFromNow(-120),
    expiresAt: daysFromNow(245),
    issuer: "Entidad de certificación digital",
    validityYears: 1,
  },
  {
    id: "c_02",
    type: "facturacion",
    holder: "Coraline Medicina Antiedad S.A.S.",
    documentNumber: "901.234.567-8",
    serial: "A9:BC:77:90:EE:04",
    status: "expiring",
    issuedAt: daysFromNow(-330),
    expiresAt: daysFromNow(34),
    issuer: "Entidad de certificación digital",
    validityYears: 1,
  },
  {
    id: "c_03",
    type: "firma_representante",
    holder: "María Fernanda Gómez (Rep. Legal)",
    documentNumber: "1.045.678.900",
    serial: "—",
    status: "pending",
    issuedAt: daysFromNow(-4),
    expiresAt: daysFromNow(361),
    issuer: "Entidad de certificación digital",
    validityYears: 1,
  },
  {
    id: "c_04",
    type: "firma_natural",
    holder: "María Fernanda Gómez",
    documentNumber: "1.045.678.900",
    serial: "1D:89:32:FF:AA:70",
    status: "expired",
    issuedAt: daysFromNow(-720),
    expiresAt: daysFromNow(-45),
    issuer: "Entidad de certificación digital",
    validityYears: 2,
  },
];

export const mockDocuments: UploadedDocument[] = [
  {
    id: "d_01",
    name: "cedula-maria-gomez.pdf",
    type: "cedula",
    sizeBytes: 842_123,
    uploadedAt: daysFromNow(-60),
  },
  {
    id: "d_02",
    name: "rut-2026-coraline.pdf",
    type: "rut",
    sizeBytes: 312_998,
    uploadedAt: daysFromNow(-12),
  },
  {
    id: "d_03",
    name: "camara-comercio-2026.pdf",
    type: "camara_comercio",
    sizeBytes: 1_245_400,
    uploadedAt: daysFromNow(-12),
  },
];

export const mockTimeline: Record<string, TimelineEvent[]> = {
  c_01: [
    { id: "t1", at: daysFromNow(-120), kind: "issued", summary: "Certificado emitido por la entidad de certificación digital." },
    { id: "t2", at: daysFromNow(-120), kind: "csr_generated", summary: "CSR firmada y PFX generado." },
    { id: "t3", at: daysFromNow(-122), kind: "docs_verified", summary: "Documentos verificados por nuestro equipo." },
    { id: "t4", at: daysFromNow(-123), kind: "requested", summary: "Solicitud creada." },
  ],
  c_02: [
    { id: "t1", at: daysFromNow(-330), kind: "issued", summary: "Certificado emitido por la entidad de certificación digital." },
    { id: "t2", at: daysFromNow(-333), kind: "docs_verified", summary: "Documentos verificados." },
    { id: "t3", at: daysFromNow(-334), kind: "requested", summary: "Solicitud creada." },
  ],
  c_03: [
    { id: "t1", at: daysFromNow(-3), kind: "docs_verified", summary: "Documentos recibidos y en verificación." },
    { id: "t2", at: daysFromNow(-4), kind: "requested", summary: "Solicitud creada." },
  ],
  c_04: [
    { id: "t1", at: daysFromNow(-45), kind: "revoked", summary: "Certificado venció." },
    { id: "t2", at: daysFromNow(-720), kind: "issued", summary: "Certificado emitido por la entidad de certificación digital." },
  ],
};

export const mockActivity: Activity[] = [
  { id: "a1", at: daysFromNow(-1), icon: "upload", text: "Subiste RUT 2026" },
  { id: "a2", at: daysFromNow(-4), icon: "request", text: "Solicitaste un certificado de Representante Legal" },
  { id: "a3", at: daysFromNow(-12), icon: "upload", text: "Subiste Cámara de Comercio" },
  { id: "a4", at: daysFromNow(-60), icon: "download", text: "Descargaste tu PFX de persona natural" },
];
